import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import PlaceInput from "../components/PlaceInput";
import {
  FolderOpen,
  Ship,
  FileText,
  Package,
  MapPin,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Search,
  ChevronRight,
  Send,
  X,
  Building2,
  Plus,
  Trash2,
  Info,
  CreditCard,
  Container,
  Eye,
  Anchor,
  ArrowRight,
} from "lucide-react";


export default function CustomerPortalPage() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState("shipments");
  const [loading, setLoading] = useState(true);
  const [shipments, setShipments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [showBLModal, setShowBLModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // BL Tracking state
  const [blTrackingQuery, setBlTrackingQuery] = useState("");
  const [blTrackingLoading, setBlTrackingLoading] = useState(false);
  const [blTrackingResult, setBlTrackingResult] = useState(null);
  const [blTrackingError, setBlTrackingError] = useState(null);

  // Fetch shipments
  useEffect(() => {
    if (token) {
      fetchShipments();
    }
  }, [token]);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/customer-portal/my-shipments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShipments(response.data.shipments || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load shipments");
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/customer-portal/my-invoices`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvoices(response.data.invoices || []);
      setTotalOutstanding(response.data.total_outstanding || 0);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load invoices");
    }
  };

  // BL Tracking handler
  const handleBlTracking = async (e) => {
    e?.preventDefault();
    
    if (!blTrackingQuery.trim()) {
      setBlTrackingError("Please enter a BL number");
      return;
    }
    
    setBlTrackingLoading(true);
    setBlTrackingError(null);
    setBlTrackingResult(null);
    
    try {
      const response = await axios.get(
        `${API_URL}/lcl-status/public/tracking/${encodeURIComponent(blTrackingQuery.trim())}`
      );
      
      if (!response.data.found) {
        setBlTrackingError(response.data.message || "No shipment found with this BL number");
        setBlTrackingResult(null);
      } else {
        setBlTrackingResult(response.data);
        setBlTrackingError(null);
      }
    } catch (error) {
      setBlTrackingError("Failed to fetch tracking information. Please try again.");
    } finally {
      setBlTrackingLoading(false);
    }
  };

  const clearBlTracking = () => {
    setBlTrackingQuery("");
    setBlTrackingResult(null);
    setBlTrackingError(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  useEffect(() => {
    if (activeTab === "invoices" && invoices.length === 0) {
      fetchInvoices();
    }
  }, [activeTab]);

  const filteredShipments = shipments.filter(s => {
    const query = searchQuery.toLowerCase();
    return (
      (s.pf_number || "").toLowerCase().includes(query) ||
      (s.description || "").toLowerCase().includes(query) ||
      (s.container_number || "").toLowerCase().includes(query) ||
      (s.pol || "").toLowerCase().includes(query) ||
      (s.pod || "").toLowerCase().includes(query)
    );
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-700",
      active: "bg-blue-100 text-blue-700",
      in_transit: "bg-purple-100 text-purple-700",
      completed: "bg-green-100 text-green-700",
      delivered: "bg-green-100 text-green-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Customer Portal</h1>
          <p className="text-slate-600 mt-1">
            Welcome back, {user?.full_name || user?.company_name}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          {[
            { id: "shipments", label: "My Shipments", icon: Ship },
            { id: "quote", label: "Get a Quote", icon: MapPin },
            { id: "invoices", label: "Invoices", icon: CreditCard },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Shipments Tab */}
        {activeTab === "shipments" && (
          <div>
            {/* BL Tracking Section */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Ship className="h-6 w-6 text-white" />
                <div>
                  <h2 className="text-xl font-bold text-white">Track by BL Number</h2>
                  <p className="text-blue-100 text-sm">Enter your HBL or MBL number to check shipment status</p>
                </div>
              </div>
              
              <form onSubmit={handleBlTracking} className="flex gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Enter BL number (e.g., MSCU1234567)"
                    value={blTrackingQuery}
                    onChange={(e) => setBlTrackingQuery(e.target.value.toUpperCase())}
                    className="pl-10 h-12 bg-white"
                    data-testid="bl-tracking-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={blTrackingLoading}
                  className="h-12 px-6 bg-white text-blue-700 hover:bg-blue-50"
                  data-testid="bl-tracking-btn"
                >
                  {blTrackingLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Search className="h-5 w-5 mr-2" />
                      Track
                    </>
                  )}
                </Button>
                {(blTrackingResult || blTrackingError) && (
                  <Button 
                    type="button"
                    variant="ghost"
                    onClick={clearBlTracking}
                    className="h-12 text-white hover:bg-blue-500/30"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                )}
              </form>
            </div>

            {/* BL Tracking Error */}
            {blTrackingError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6"
              >
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-red-800">Shipment Not Found</p>
                    <p className="text-sm text-red-600">{blTrackingError}</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* BL Tracking Result */}
            {blTrackingResult && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl border shadow-sm mb-6 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-xs uppercase">BL Number</p>
                      <h3 className="text-xl font-bold font-mono">{blTrackingResult.bl_number}</h3>
                      {blTrackingResult.pf_number && (
                        <p className="text-slate-300 text-sm mt-0.5">Reference: {blTrackingResult.pf_number}</p>
                      )}
                    </div>
                    <span className={`px-4 py-1.5 rounded-full text-sm font-medium ${
                      blTrackingResult.status === "working" 
                        ? "bg-green-100 text-green-700" 
                        : "bg-orange-100 text-orange-700"
                    }`}>
                      {blTrackingResult.status === "working" ? "In Process" : "Pending Review"}
                    </span>
                  </div>
                </div>

                <div className="p-4">
                  {/* Current Status */}
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b">
                    <div className={`p-2 rounded-full ${
                      blTrackingResult.status === "working" ? "bg-green-100" : "bg-orange-100"
                    }`}>
                      {blTrackingResult.status === "working" ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <Clock className="h-5 w-5 text-orange-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Current Status</p>
                      <p className="font-semibold text-slate-800">
                        {blTrackingResult.current_status?.description || "Processing"}
                      </p>
                      {blTrackingResult.current_status?.timestamp && (
                        <p className="text-xs text-slate-400">
                          Last updated: {formatDate(blTrackingResult.current_status.timestamp)}
                        </p>
                      )}
                    </div>
                  </div>

                  {blTrackingResult.note && (
                    <div className="bg-orange-50 rounded-lg p-3 mb-4">
                      <p className="text-sm text-orange-700">{blTrackingResult.note}</p>
                    </div>
                  )}

                  {/* Shipment Info Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase">Origin</p>
                      <p className="font-semibold text-slate-800 flex items-center gap-1 mt-0.5">
                        <Anchor className="h-3 w-3 text-slate-400" />
                        {blTrackingResult.shipment_info?.origin_port || "-"}
                      </p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase">Destination</p>
                      <p className="font-semibold text-slate-800 flex items-center gap-1 mt-0.5">
                        <MapPin className="h-3 w-3 text-slate-400" />
                        {blTrackingResult.shipment_info?.destination_port || "-"}
                      </p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase">Vessel</p>
                      <p className="font-semibold text-slate-800 flex items-center gap-1 mt-0.5">
                        <Ship className="h-3 w-3 text-slate-400" />
                        {blTrackingResult.shipment_info?.vessel_name || "-"}
                      </p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase">ETA</p>
                      <p className="font-semibold text-slate-800 flex items-center gap-1 mt-0.5">
                        <Calendar className="h-3 w-3 text-slate-400" />
                        {blTrackingResult.shipment_info?.eta ? formatDate(blTrackingResult.shipment_info.eta) : "-"}
                      </p>
                    </div>
                  </div>

                  {/* Status History */}
                  {blTrackingResult.status_history && blTrackingResult.status_history.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-medium text-slate-700 mb-3">Status History</h4>
                      <div className="space-y-2">
                        {blTrackingResult.status_history.map((event, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <div className={`mt-1.5 w-2 h-2 rounded-full ${idx === 0 ? 'bg-blue-600' : 'bg-slate-300'}`} />
                            <div className="flex-1">
                              <p className={`text-sm ${idx === 0 ? 'font-medium text-slate-800' : 'text-slate-600'}`}>
                                {event.status}
                              </p>
                              <p className="text-xs text-slate-400">
                                {formatDate(event.timestamp)}
                                {event.location && ` • ${event.location}`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Search My Shipments */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by PF number, container, route..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-shipments"
                />
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : filteredShipments.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-xl border">
                <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600">No shipments found</h3>
                <p className="text-slate-400">
                  {searchQuery ? "Try a different search term" : "Your shipments will appear here"}
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredShipments.map((shipment) => (
                  <motion.div
                    key={shipment.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl border shadow-sm hover:shadow-md transition-shadow p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-blue-600 text-lg">
                            {shipment.pf_number || shipment.job_number}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(shipment.status || shipment.tracking_status)}`}>
                            {(shipment.status || shipment.tracking_status || "pending").replace(/_/g, " ")}
                          </span>
                          {shipment.bl_instructions_received && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              BL Submitted
                            </span>
                          )}
                          {shipment.bl_instructions_status === 'requested' && !shipment.bl_instructions_received && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 animate-pulse">
                              BL Instructions Requested
                            </span>
                          )}
                        </div>
                        
                        <p className="text-slate-600 mt-1 text-sm">
                          {shipment.description || shipment.commodity || "-"}
                        </p>

                        <div className="flex items-center gap-6 mt-3 text-sm text-slate-500">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{shipment.pol || "-"}</span>
                            <ChevronRight className="w-4 h-4" />
                            <span>{shipment.pod || "-"}</span>
                          </div>
                          {shipment.container_number && (
                            <div className="flex items-center gap-1">
                              <Container className="w-4 h-4" />
                              <span className="font-mono">{shipment.container_number}</span>
                            </div>
                          )}
                          {shipment.eta && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>ETA: {new Date(shipment.eta).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {shipment.bl_instructions_status === 'requested' && !shipment.bl_instructions_received && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedShipment(shipment);
                              setShowBLModal(true);
                            }}
                            className="bg-amber-500 hover:bg-amber-600 animate-pulse"
                            data-testid={`submit-bl-btn-${shipment.id}`}
                          >
                            <FileText className="w-4 h-4 mr-1" />
                            Submit BL Instructions
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedShipment(shipment)}
                          data-testid={`view-details-btn-${shipment.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === "quote" && <SpotQuoteTab token={token} user={user} />}

        {activeTab === "invoices" && (
          <div>
            {/* Summary Card */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white mb-6">
              <p className="text-blue-100 text-sm">Total Outstanding</p>
              <p className="text-4xl font-bold mt-1">€{totalOutstanding.toLocaleString()}</p>
            </div>

            {invoices.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-xl border">
                <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600">No invoices found</h3>
              </div>
            ) : (
              <div className="bg-white rounded-xl border overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Invoice #</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">PF Reference</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Date</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Due Date</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Amount</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Outstanding</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice, idx) => (
                      <tr key={invoice.id || idx} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium">{invoice.invoice_number}</td>
                        <td className="px-4 py-3 text-slate-600">{invoice.pf_number || "-"}</td>
                        <td className="px-4 py-3 text-slate-600">{invoice.invoice_date || "-"}</td>
                        <td className="px-4 py-3 text-slate-600">{invoice.due_date || "-"}</td>
                        <td className="px-4 py-3 text-right">€{invoice.amount?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-medium text-red-600">
                          €{invoice.outstanding?.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            invoice.status === "paid" ? "bg-green-100 text-green-700" :
                            invoice.status === "overdue" ? "bg-red-100 text-red-700" :
                            "bg-yellow-100 text-yellow-700"
                          }`}>
                            {invoice.status || "open"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Shipment Details Modal */}
        <AnimatePresence>
          {selectedShipment && !showBLModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedShipment(null)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                  <h2 className="text-xl font-bold">Shipment Details</h2>
                  <Button variant="ghost" size="icon" onClick={() => setSelectedShipment(null)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <div className="p-6 space-y-6">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-blue-600">
                      {selectedShipment.pf_number || selectedShipment.job_number}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedShipment.status)}`}>
                      {selectedShipment.status || "pending"}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500">Description</Label>
                      <p className="font-medium">{selectedShipment.description || "-"}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500">Container</Label>
                      <p className="font-mono font-medium">{selectedShipment.container_number || "-"}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500">Port of Loading</Label>
                      <p className="font-medium">{selectedShipment.pol || "-"}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500">Port of Discharge</Label>
                      <p className="font-medium">{selectedShipment.pod || "-"}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500">ETD</Label>
                      <p className="font-medium">{selectedShipment.etd || "-"}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500">ETA</Label>
                      <p className="font-medium">{selectedShipment.eta || "-"}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500">Vessel</Label>
                      <p className="font-medium">{selectedShipment.vessel || "-"}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500">BL Number</Label>
                      <p className="font-mono font-medium">{selectedShipment.bl_number || "-"}</p>
                    </div>
                  </div>

                  {!selectedShipment.bl_instructions_received && (
                    <Button
                      onClick={() => setShowBLModal(true)}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Submit BL Instructions
                    </Button>
                  )}

                  {selectedShipment.bl_instructions_received && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-700">BL Instructions Submitted</p>
                        <p className="text-sm text-green-600">
                          {selectedShipment.bl_instructions_received_at 
                            ? `Submitted on ${new Date(selectedShipment.bl_instructions_received_at).toLocaleDateString()}`
                            : "Your instructions are being processed"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* BL Instructions Modal */}
        <AnimatePresence>
          {showBLModal && selectedShipment && (
            <BLInstructionsModal
              shipment={selectedShipment}
              token={token}
              onClose={() => {
                setShowBLModal(false);
                setSelectedShipment(null);
              }}
              onSuccess={() => {
                setShowBLModal(false);
                setSelectedShipment(null);
                fetchShipments();
              }}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// BL Instructions Modal Component
function BLInstructionsModal({ shipment, token, onClose, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [blType, setBlType] = useState("hbl_only");
  const [formData, setFormData] = useState({
    shipper_name: "",
    shipper_address: "",
    shipper_city: "",
    shipper_country: "",
    shipper_contact: "",
    shipper_phone: "",
    shipper_email: "",
    consignee_name: "",
    consignee_address: "",
    consignee_city: "",
    consignee_country: "",
    consignee_contact: "",
    consignee_phone: "",
    consignee_email: "",
    notify_same_as_consignee: false,
    notify_name: "",
    notify_address: "",
    notify_city: "",
    notify_country: "",
    notify_contact: "",
    notify_phone: "",
    vessel_name: shipment.vessel || "",
    voyage_number: shipment.voyage || "",
    port_of_loading: shipment.pol || "",
    port_of_discharge: shipment.pod || "",
    place_of_receipt: "",
    place_of_delivery: "",
    cargo_lines: [{
      container_number: shipment.container_number || "",
      seal_number: "",
      marks_numbers: "",
      package_count: "",
      package_type: "CARTONS",
      description: shipment.description || "",
      gross_weight_kg: shipment.weight || "",
      volume_cbm: shipment.volume || "",
      hs_code: ""
    }],
    freight_terms: "PREPAID",
    incoterm: "FOB",
    declared_value: "",
    special_instructions: "",
    mbl_shipper_name: "",
    mbl_shipper_address: "",
    mbl_consignee_name: "",
    mbl_consignee_address: "",
    mbl_notify_name: "",
    mbl_notify_address: "",
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateCargoLine = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      cargo_lines: prev.cargo_lines.map((line, i) => 
        i === index ? { ...line, [field]: value } : line
      )
    }));
  };

  const addCargoLine = () => {
    setFormData(prev => ({
      ...prev,
      cargo_lines: [...prev.cargo_lines, {
        container_number: "",
        seal_number: "",
        marks_numbers: "",
        package_count: "",
        package_type: "CARTONS",
        description: "",
        gross_weight_kg: "",
        volume_cbm: "",
        hs_code: ""
      }]
    }));
  };

  const removeCargoLine = (index) => {
    if (formData.cargo_lines.length > 1) {
      setFormData(prev => ({
        ...prev,
        cargo_lines: prev.cargo_lines.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.shipper_name || !formData.consignee_name) {
      toast.error("Please fill in shipper and consignee names");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/customer-portal/shipment/${shipment.id}/bl-instructions`,
        { bl_type: blType, ...formData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("BL Instructions submitted successfully!");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit BL instructions");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-bold">Submit BL Instructions</h2>
            <p className="text-sm text-slate-500">Reference: {shipment.pf_number}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* BL Type Selection */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Ship className="w-5 h-5 text-blue-600" />
              Bill of Lading Type
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <label 
                className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  blType === "hbl_only" 
                    ? "border-blue-500 bg-blue-50" 
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="blType"
                  value="hbl_only"
                  checked={blType === "hbl_only"}
                  onChange={(e) => setBlType(e.target.value)}
                  className="sr-only"
                />
                <div className="font-semibold">House BL Only (HBL)</div>
                <p className="text-sm text-gray-600 mt-1">
                  Europacific issues the Bill of Lading
                </p>
              </label>
              <label 
                className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  blType === "mbl_and_hbl" 
                    ? "border-blue-500 bg-blue-50" 
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="blType"
                  value="mbl_and_hbl"
                  checked={blType === "mbl_and_hbl"}
                  onChange={(e) => setBlType(e.target.value)}
                  className="sr-only"
                />
                <div className="font-semibold">Master BL + House BL</div>
                <p className="text-sm text-gray-600 mt-1">
                  Shipping line issues MBL, we issue HBL
                </p>
              </label>
            </div>
          </div>

          {/* Shipper Details */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Building2 className="w-5 h-5 text-green-600" />
              Shipper / Exporter
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Company Name *</Label>
                <Input
                  value={formData.shipper_name}
                  onChange={(e) => updateField("shipper_name", e.target.value)}
                  placeholder="Full legal company name"
                  data-testid="shipper-name-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Textarea
                  value={formData.shipper_address}
                  onChange={(e) => updateField("shipper_address", e.target.value)}
                  placeholder="Street address"
                  rows={2}
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={formData.shipper_city}
                  onChange={(e) => updateField("shipper_city", e.target.value)}
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={formData.shipper_country}
                  onChange={(e) => updateField("shipper_country", e.target.value)}
                />
              </div>
              <div>
                <Label>Contact Person</Label>
                <Input
                  value={formData.shipper_contact}
                  onChange={(e) => updateField("shipper_contact", e.target.value)}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.shipper_phone}
                  onChange={(e) => updateField("shipper_phone", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Consignee Details */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              Consignee
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Company Name *</Label>
                <Input
                  value={formData.consignee_name}
                  onChange={(e) => updateField("consignee_name", e.target.value)}
                  placeholder="Full legal company name"
                  data-testid="consignee-name-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Textarea
                  value={formData.consignee_address}
                  onChange={(e) => updateField("consignee_address", e.target.value)}
                  placeholder="Street address"
                  rows={2}
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={formData.consignee_city}
                  onChange={(e) => updateField("consignee_city", e.target.value)}
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={formData.consignee_country}
                  onChange={(e) => updateField("consignee_country", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Notify Party */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Building2 className="w-5 h-5 text-orange-600" />
              Notify Party
            </Label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.notify_same_as_consignee}
                onChange={(e) => {
                  updateField("notify_same_as_consignee", e.target.checked);
                  if (e.target.checked) {
                    updateField("notify_name", formData.consignee_name);
                    updateField("notify_address", formData.consignee_address);
                    updateField("notify_city", formData.consignee_city);
                    updateField("notify_country", formData.consignee_country);
                  }
                }}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm">Same as Consignee</span>
            </label>
            {!formData.notify_same_as_consignee && (
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Company Name</Label>
                  <Input
                    value={formData.notify_name}
                    onChange={(e) => updateField("notify_name", e.target.value)}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Address</Label>
                  <Textarea
                    value={formData.notify_address}
                    onChange={(e) => updateField("notify_address", e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            )}
          </div>

          {/* MBL Details (if MBL+HBL selected) */}
          {blType === "mbl_and_hbl" && (
            <div className="space-y-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <Label className="text-base font-semibold flex items-center gap-2 text-amber-800">
                <Info className="w-5 h-5" />
                Master BL Details (For Shipping Line)
              </Label>
              <p className="text-sm text-amber-700 mb-3">
                These details will be used for the carrier's Master Bill of Lading.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>MBL Shipper Name</Label>
                  <Input
                    value={formData.mbl_shipper_name}
                    onChange={(e) => updateField("mbl_shipper_name", e.target.value)}
                    placeholder="Leave blank if same as HBL"
                  />
                </div>
                <div>
                  <Label>MBL Consignee</Label>
                  <Input
                    value={formData.mbl_consignee_name}
                    onChange={(e) => updateField("mbl_consignee_name", e.target.value)}
                    placeholder="Often: TO ORDER or Europacific"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Shipment Details */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Shipment Details
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vessel Name</Label>
                <Input
                  value={formData.vessel_name}
                  onChange={(e) => updateField("vessel_name", e.target.value)}
                />
              </div>
              <div>
                <Label>Voyage Number</Label>
                <Input
                  value={formData.voyage_number}
                  onChange={(e) => updateField("voyage_number", e.target.value)}
                />
              </div>
              <div>
                <Label>Port of Loading</Label>
                <Input
                  value={formData.port_of_loading}
                  onChange={(e) => updateField("port_of_loading", e.target.value)}
                />
              </div>
              <div>
                <Label>Port of Discharge</Label>
                <Input
                  value={formData.port_of_discharge}
                  onChange={(e) => updateField("port_of_discharge", e.target.value)}
                />
              </div>
              <div>
                <Label>Freight Terms</Label>
                <select
                  value={formData.freight_terms}
                  onChange={(e) => updateField("freight_terms", e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                >
                  <option value="PREPAID">Freight Prepaid</option>
                  <option value="COLLECT">Freight Collect</option>
                </select>
              </div>
              <div>
                <Label>Incoterm</Label>
                <select
                  value={formData.incoterm}
                  onChange={(e) => updateField("incoterm", e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                >
                  <option value="EXW">EXW</option>
                  <option value="FCA">FCA</option>
                  <option value="FOB">FOB</option>
                  <option value="CFR">CFR</option>
                  <option value="CIF">CIF</option>
                  <option value="DAP">DAP</option>
                  <option value="DDP">DDP</option>
                </select>
              </div>
            </div>
          </div>

          {/* Cargo Details */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Package className="w-5 h-5 text-indigo-600" />
                Cargo Details
              </Label>
              <Button size="sm" variant="outline" onClick={addCargoLine}>
                <Plus className="w-4 h-4 mr-1" />
                Add Container
              </Button>
            </div>
            
            {formData.cargo_lines.map((line, index) => (
              <div key={index} className="p-4 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-700">Container {index + 1}</span>
                  {formData.cargo_lines.length > 1 && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => removeCargoLine(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Container No.</Label>
                    <Input
                      value={line.container_number}
                      onChange={(e) => updateCargoLine(index, "container_number", e.target.value)}
                      placeholder="ABCD1234567"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Seal No.</Label>
                    <Input
                      value={line.seal_number}
                      onChange={(e) => updateCargoLine(index, "seal_number", e.target.value)}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Packages</Label>
                    <Input
                      value={line.package_count}
                      onChange={(e) => updateCargoLine(index, "package_count", e.target.value)}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Gross Weight (KG)</Label>
                    <Input
                      value={line.gross_weight_kg}
                      onChange={(e) => updateCargoLine(index, "gross_weight_kg", e.target.value)}
                      className="text-sm"
                    />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">Description of Goods</Label>
                    <Textarea
                      value={line.description}
                      onChange={(e) => updateCargoLine(index, "description", e.target.value)}
                      rows={2}
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Special Instructions */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Special Instructions</Label>
            <Textarea
              value={formData.special_instructions}
              onChange={(e) => updateField("special_instructions", e.target.value)}
              placeholder="Any special instructions or additional notes..."
              rows={3}
            />
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting}
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid="submit-bl-instructions-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {submitting ? "Submitting..." : "Submit BL Instructions"}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function SpotQuoteTab({ token, user }) {
  const [form, setForm] = useState({ mode: "fcl", pol: "", pod: "", incoterm: "FOB", equipment: "40HC", qty: 1, cbm: 1 });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const getPrice = async () => {
    if (!form.pol.trim() || !form.pod.trim()) { toast.error("Please enter POL and POD"); return; }
    setLoading(true); setResult(null); setAccepted(null);
    try {
      const { data } = await axios.post(`${API_URL}/customer-portal/spot-quote`, form, { headers: { Authorization: `Bearer ${token}` } });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to get price");
    } finally { setLoading(false); }
  };

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await axios.post(`${API_URL}/customer-portal/spot-quote/accept`, {
        mode: result.mode, pol: result.pol, pod: result.pod, currency: result.currency,
        freight_label: result.freight_label, freight_total: result.freight_total, transit_days: result.transit_days,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAccepted(data);
      toast.success("Offer generated and emailed to you");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate offer");
    } finally { setAccepting(false); }
  };

  const inputCls = "w-full h-10 px-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div data-testid="spot-quote-tab" className="max-w-3xl">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 mb-6">
        <h2 className="text-xl font-bold text-white">Instant Spot Quote</h2>
        <p className="text-blue-100 text-sm">Live sea-freight pricing, all-in. Door-to-door (EXW/DAP) shipments are quoted by our team.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div className="flex gap-2">
          {["fcl", "lcl"].map(m => (
            <button key={m} onClick={() => set("mode", m)} data-testid={`spot-quote-mode-${m}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium border ${form.mode === m ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300"}`}>
              {m.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500">Port of Loading (POL)</label>
            <PlaceInput
              value={form.pol}
              kind="sea"
              placeholder="Port e.g. Shanghai / Koper"
              data-testid="spot-quote-pol"
              onSelect={(p) => set("pol", p.code || p.name || "")}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Port of Discharge (POD)</label>
            <PlaceInput
              value={form.pod}
              kind="sea"
              placeholder="Port e.g. Koper / Shanghai"
              data-testid="spot-quote-pod"
              onSelect={(p) => set("pod", p.code || p.name || "")}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Incoterm</label>
            <select className={inputCls} value={form.incoterm} onChange={e => set("incoterm", e.target.value)} data-testid="spot-quote-incoterm">
              <optgroup label="Port-to-port (instant)">
                <option value="FOB">FOB</option>
                <option value="CFR">CFR</option>
                <option value="CIF">CIF</option>
              </optgroup>
              <optgroup label="Door (quoted by our team)">
                <option value="EXW">EXW</option>
                <option value="DAP">DAP</option>
                <option value="DDP">DDP</option>
              </optgroup>
            </select>
          </div>
          {form.mode === "fcl" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Equipment</label>
                <select className={inputCls} value={form.equipment} onChange={e => set("equipment", e.target.value)} data-testid="spot-quote-equipment">
                  <option value="20GP">20' GP</option>
                  <option value="40GP">40' GP</option>
                  <option value="40HC">40' HC</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500">Qty</label>
                <input type="number" min="1" className={inputCls} value={form.qty} onChange={e => set("qty", parseInt(e.target.value) || 1)} data-testid="spot-quote-qty" />
              </div>
            </div>
          ) : (
            <div>
              <label className="text-xs text-slate-500">Volume (W/M — CBM or tons)</label>
              <input type="number" min="0.1" step="0.1" className={inputCls} value={form.cbm} onChange={e => set("cbm", parseFloat(e.target.value) || 1)} data-testid="spot-quote-cbm" />
            </div>
          )}
        </div>

        <Button onClick={getPrice} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="spot-quote-get-btn">
          {loading ? "Getting price…" : "Get instant price"}
        </Button>
      </div>

      {result && (
        <div className="mt-5 bg-white rounded-xl border border-slate-200 p-5" data-testid="spot-quote-result">
          {result.manual ? (
            <div className="text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm" data-testid="spot-quote-manual">
              {result.message}
            </div>
          ) : result.found === false ? (
            <div className="text-slate-600 text-sm" data-testid="spot-quote-none">{result.message}</div>
          ) : (
            <div>
              <div className="flex items-baseline justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">All-in ocean freight</p>
                  <p className="text-3xl font-bold text-slate-800" data-testid="spot-quote-total">
                    {result.freight_total?.toLocaleString()} {result.currency}
                  </p>
                </div>
                {result.transit_days && <span className="text-sm text-slate-500">~{result.transit_days} days</span>}
              </div>
              <p className="text-sm text-slate-600 mt-1">{result.freight_label}</p>

              {result.components?.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Detailed breakdown</p>
                  <ul className="text-sm text-slate-600 space-y-1" data-testid="spot-quote-breakdown">
                    {result.components.map((c, i) => (
                      <li key={i} className="flex justify-between border-b border-slate-100 py-1">
                        <span>{c.name} <span className="text-slate-400 text-xs">({c.quantity}× /{c.unit})</span></span>
                        <span className={c.total > 0 ? "font-medium" : "text-slate-400"}>
                          {c.total > 0 ? `${c.total.toLocaleString()} ${c.currency}` : "quoted at booking"}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-slate-400 mt-1">Full itemised quotation (same as the PDF) is emailed when you request the offer.</p>
                </div>
              )}

              <p className="text-xs text-slate-400 mt-3">Valid {result.valid_days} days. Rate subject to space & final documentation.</p>

              {accepted ? (
                <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800" data-testid="spot-quote-accepted">
                  ✓ Offer <strong>{accepted.offer_number}</strong> generated. {accepted.message}
                </div>
              ) : (
                <Button onClick={accept} disabled={accepting} className="mt-4 bg-green-600 hover:bg-green-700" data-testid="spot-quote-accept-btn">
                  {accepting ? "Generating…" : "Request this offer (email me the PDF)"}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

